import { getDatabase, ref, get } from 'firebase/database';
import { app } from '../firebase';
import {
  getInstitutionKey,
  getQuizBankVersionPaths,
  getContentPath,
  getSpecialtyPaths,
  isSemar,
  resolveInstitucion,
} from '../config/institutions';
import {
  getCachedData,
  setCachedData,
  buildCacheKey,
  getCachedDataWithVersion,
  getCachedEntry,
  isCacheValid,
  getIdbCachedData,
  setIdbCachedData,
  removeIdbCachedData,
  removeCachedData,
  removeCachePrefix,
  clearIdbCache,
} from '../utils/cache';

const CACHE_TTL = {
  STATIC: 7 * 24 * 60 * 60 * 1000,
  CONFIG: 24 * 60 * 60 * 1000,
  USER_SPECIFIC: 24 * 60 * 60 * 1000,
  SHORT: 5 * 60 * 1000,
};

const QUIZ_BANK_IDB_KEY = 'quiz_bank_v1';
const QUIZ_BANK_LEGACY_KEYS = [
  'areas__legacy',
  'temas__legacy',
  'preguntas_por_tema__legacy',
  'areas',
  'temas',
  'preguntas_legacy',
  'preguntas',
];
const QUIZ_BANK_REVALIDATE_MS = 5 * 60 * 1000;
const NON_SEMAR_BANK_TTL = CACHE_TTL.STATIC;

// Un estado de caché por institución: SEMAR conserva la clave histórica y
// GN/SEDENA usan llaves propias para no mezclar bancos.
const _quizBankStates = new Map();
const _revalidatingKeys = new Set();
const _especInFlight = new Map();

function getBankState(institucion) {
  const key = getInstitutionKey(institucion);
  if (!_quizBankStates.has(key)) {
    _quizBankStates.set(key, {
      memory: null,
      promise: null,
      revalidationPromise: null,
      lastCheck: 0,
      revalidating: false,
    });
  }
  return _quizBankStates.get(key);
}

function getBankCacheKey(institucion) {
  const key = getInstitutionKey(institucion);
  return key === 'semar' ? QUIZ_BANK_IDB_KEY : `${QUIZ_BANK_IDB_KEY}_${key}`;
}

function getBankIdbTtl(institucion) {
  return isSemar(institucion) ? 0 : NON_SEMAR_BANK_TTL;
}

function rtdb() {
  return getDatabase(app);
}

function toNumericId(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? value : parsed;
}

function normalizeAreas(value) {
  if (!value) return [];
  const entries = Array.isArray(value)
    ? value.map((area, index) => [area?.id ?? index, area])
    : Object.entries(value);
  return entries
    .filter(([, area]) => area && typeof area === 'object')
    .map(([id, area]) => ({ ...area, id: toNumericId(id) }));
}

function normalizeTemas(value) {
  if (!value) return [];
  const entries = Array.isArray(value)
    ? value.map((tema, index) => [tema?.id ?? index, tema])
    : Object.entries(value);
  return entries
    .filter(([, tema]) => tema && typeof tema === 'object')
    .map(([id, tema]) => ({ ...tema, id: toNumericId(id) }));
}

function buildQuestionId(temaId, rawId) {
  const raw = rawId === undefined || rawId === null ? '' : String(rawId);
  if (temaId === undefined || temaId === null || temaId === '') return raw;
  if (!raw) return String(temaId);
  if (raw.startsWith('q_') || raw.startsWith(`${temaId}_`)) return raw;
  return `${temaId}_${raw}`;
}

function normalizePreguntas(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .filter((question) => question && typeof question === 'object')
      .map((question, index) => {
        const rawTemaId = question.tema_id ?? question.temaId;
        const temaId = rawTemaId === undefined || rawTemaId === null ? undefined : toNumericId(rawTemaId);
        return {
          ...question,
          id: buildQuestionId(temaId, question.id ?? index),
          tema_id: temaId,
        };
      });
  }

  if (typeof value !== 'object') return [];

  const preguntas = [];
  Object.entries(value).forEach(([temaIdRaw, preguntasMap]) => {
    if (!preguntasMap) return;
    const temaId = toNumericId(temaIdRaw);

    if (Array.isArray(preguntasMap)) {
      preguntasMap.forEach((question, index) => {
        if (!question || typeof question !== 'object') return;
        const rawTemaId = question.tema_id ?? question.temaId;
        const resolvedTemaId = rawTemaId === undefined || rawTemaId === null ? temaId : toNumericId(rawTemaId);
        preguntas.push({
          ...question,
          id: buildQuestionId(resolvedTemaId, question.id ?? index),
          tema_id: resolvedTemaId,
        });
      });
      return;
    }

    if (typeof preguntasMap === 'object') {
      Object.entries(preguntasMap).forEach(([questionId, question]) => {
        if (!question || typeof question !== 'object') return;
        const rawTemaId = question.tema_id ?? question.temaId;
        const resolvedTemaId = rawTemaId === undefined || rawTemaId === null ? temaId : toNumericId(rawTemaId);
        preguntas.push({
          ...question,
          id: buildQuestionId(resolvedTemaId, questionId),
          tema_id: resolvedTemaId,
        });
      });
    }
  });
  return preguntas;
}

function inferTemaId(question, temas) {
  const candidates = [question.tema_id, question.temaId, question.tema];
  for (const raw of candidates) {
    if (raw === undefined || raw === null || raw === '') continue;
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed) && (typeof raw === 'number' || String(parsed) === String(raw).trim())) {
      return parsed;
    }
    const name = String(raw).toLowerCase().trim();
    const match = temas.find((tema) => {
      const temaName = (tema.nombre || '').toLowerCase().trim();
      return temaName && (name === temaName || name.includes(temaName) || temaName.includes(name));
    });
    if (match) return toNumericId(match.id);
  }
  return undefined;
}

function normalizeLegacyPreguntas(value, temas) {
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value)
    .map(([id, question]) => {
      if (!question || typeof question !== 'object') return null;
      return {
        ...question,
        id,
        tema_id: inferTemaId(question, temas),
      };
    })
    .filter(Boolean);
}

async function fetchVersionNode(path) {
  try {
    const snapshot = await get(ref(rtdb(), path));
    return snapshot.exists() ? snapshot.val() : null;
  } catch {
    return null;
  }
}

async function fetchQuizBankVersion(institucion) {
  const paths = getQuizBankVersionPaths(institucion);
  if (paths.length === 0) return null;
  const values = await Promise.all(paths.map(fetchVersionNode));
  if (values.every((value) => value == null)) return null;
  return values.map((value) => (value == null ? '' : String(value))).join('|');
}

/**
 * GN/SEDENA guardan cada tema como { area_id, id, preguntas: { qid: {...} } }.
 * Se desenvuelve `preguntas` y se conserva el id del tema para enlazar con `temas`.
 */
function normalizeInstitutionPreguntas(value) {
  if (!value || typeof value !== 'object') return [];

  const preguntas = [];
  Object.entries(value).forEach(([temaIdRaw, bank]) => {
    if (!bank || typeof bank !== 'object') return;
    const temaId = toNumericId(temaIdRaw);
    const rawPreguntas = bank.preguntas ?? bank;
    if (!rawPreguntas || typeof rawPreguntas !== 'object') return;

    const entries = Array.isArray(rawPreguntas)
      ? rawPreguntas.map((question, index) => [question?.id ?? index, question])
      : Object.entries(rawPreguntas);

    entries.forEach(([questionId, question]) => {
      if (!question || typeof question !== 'object') return;
      const rawTemaId = question.tema_id ?? question.temaId;
      const resolvedTemaId = rawTemaId === undefined || rawTemaId === null ? temaId : toNumericId(rawTemaId);
      preguntas.push({
        ...question,
        id: buildQuestionId(resolvedTemaId, question.id ?? questionId),
        tema_id: resolvedTemaId,
      });
    });
  });

  return preguntas;
}

async function fetchQuizBankRemote(institucion = 'SEMAR') {
  const database = rtdb();
  const [version, areasSnapshot, temasSnapshot, preguntasSnapshot] = await Promise.all([
    fetchQuizBankVersion(institucion),
    get(ref(database, getContentPath(institucion, 'areas'))),
    get(ref(database, getContentPath(institucion, 'temas'))),
    get(ref(database, getContentPath(institucion, 'preguntas_por_tema'))),
  ]);

  const areas = normalizeAreas(areasSnapshot.exists() ? areasSnapshot.val() : null);
  const temas = normalizeTemas(temasSnapshot.exists() ? temasSnapshot.val() : null);
  const rawPreguntas = preguntasSnapshot.exists() ? preguntasSnapshot.val() : null;
  let preguntas = isSemar(institucion)
    ? normalizePreguntas(rawPreguntas)
    : normalizeInstitutionPreguntas(rawPreguntas);

  if (preguntas.length === 0) {
    try {
      const legacySnapshot = await get(ref(database, getContentPath(institucion, 'preguntas')));
      if (legacySnapshot.exists()) {
        preguntas = normalizeLegacyPreguntas(legacySnapshot.val(), temas);
      }
    } catch {}
  }

  return { areas, temas, preguntas, version, timestamp: Date.now() };
}

function readLegacyQuizBank() {
  const candidates = [
    ['areas__legacy', 'temas__legacy', 'preguntas_por_tema__legacy'],
    ['areas', 'temas', 'preguntas_legacy'],
    ['areas', 'temas', 'preguntas'],
  ];

  for (const keys of candidates) {
    const entries = keys.map((key) => getCachedEntry(key));
    if (entries.some((entry) => !entry)) continue;

    const [areasEntry, temasEntry, preguntasEntry] = entries;
    const areas = normalizeAreas(areasEntry.data);
    const temas = normalizeTemas(temasEntry.data);
    const preguntas = normalizePreguntas(preguntasEntry.data);
    if (areas.length === 0 || temas.length === 0 || preguntas.length === 0) continue;

    const timestamps = entries.map((entry) => entry.timestamp).filter(Boolean);
    return {
      areas,
      temas,
      preguntas,
      version: preguntasEntry.version ?? temasEntry.version ?? areasEntry.version ?? null,
      timestamp: timestamps.length > 0 ? Math.min(...timestamps) : Date.now(),
    };
  }
  return null;
}

async function persistQuizBank(institucion, bank) {
  const state = getBankState(institucion);
  state.memory = bank;
  const saved = await setIdbCachedData(
    getBankCacheKey(institucion),
    { areas: bank.areas, temas: bank.temas, preguntas: bank.preguntas },
    bank.version
  );
  if (saved && isSemar(institucion)) {
    QUIZ_BANK_LEGACY_KEYS.forEach((key) => removeCachedData(key));
  }
  return saved;
}

function scheduleQuizBankRevalidation(institucion) {
  const state = getBankState(institucion);
  if (state.revalidating) return;
  if (Date.now() - state.lastCheck < QUIZ_BANK_REVALIDATE_MS) return;
  state.revalidating = true;
  revalidateQuizBank(institucion)
    .catch(() => {})
    .finally(() => {
      state.revalidating = false;
    });
}

export async function loadQuizBank({ forceRefresh = false, institucion = 'SEMAR' } = {}) {
  const resolved = resolveInstitucion(institucion);
  const state = getBankState(resolved);

  if (forceRefresh) {
    state.memory = null;
    state.lastCheck = 0;
  }
  if (state.memory) {
    scheduleQuizBankRevalidation(resolved);
    return state.memory;
  }
  if (state.promise) return state.promise;

  state.promise = (async () => {
    const idbEntry = await getIdbCachedData(getBankCacheKey(resolved), getBankIdbTtl(resolved));
    if (idbEntry?.data) {
      const bank = {
        areas: idbEntry.data.areas || [],
        temas: idbEntry.data.temas || [],
        preguntas: idbEntry.data.preguntas || [],
        version: idbEntry.version ?? null,
        timestamp: idbEntry.timestamp || Date.now(),
      };
      state.memory = bank;
      scheduleQuizBankRevalidation(resolved);
      return bank;
    }

    if (isSemar(resolved)) {
      const legacy = readLegacyQuizBank();
      if (legacy) {
        state.memory = legacy;
        persistQuizBank(resolved, legacy).catch(() => {});
        scheduleQuizBankRevalidation(resolved);
        return legacy;
      }
    }

    try {
      const bank = await fetchQuizBankRemote(resolved);
      state.lastCheck = Date.now();
      await persistQuizBank(resolved, bank);
      return bank;
    } catch (error) {
      console.warn(`[rtdbCache] No se pudo cargar el banco de preguntas (${resolved}):`, error);
      return { areas: [], temas: [], preguntas: [], version: null, timestamp: 0 };
    }
  })();

  try {
    return await state.promise;
  } finally {
    state.promise = null;
  }
}

export async function revalidateQuizBank(institucion = 'SEMAR') {
  const resolved = resolveInstitucion(institucion);
  const state = getBankState(resolved);
  if (!state.memory) return null;
  if (state.revalidationPromise) return state.revalidationPromise;

  state.revalidationPromise = (async () => {
    try {
      const remoteVersion = await fetchQuizBankVersion(resolved);
      const current = state.memory;
      const versionChanged = remoteVersion != null && current?.version !== remoteVersion;
      const missingData = !current || current.preguntas.length === 0;
      const expired = !isSemar(resolved) && current?.timestamp
        ? Date.now() - current.timestamp > NON_SEMAR_BANK_TTL
        : false;
      if (!versionChanged && !missingData && !expired) return null;

      const fresh = await fetchQuizBankRemote(resolved);
      await persistQuizBank(resolved, fresh);
      return fresh;
    } catch (error) {
      console.warn(`[rtdbCache] No se pudo revalidar el banco de preguntas (${resolved}):`, error);
      return null;
    } finally {
      state.lastCheck = Date.now();
      state.revalidationPromise = null;
    }
  })();

  return state.revalidationPromise;
}

export async function prefetchQuizBank(institucion = 'SEMAR') {
  return loadQuizBank({ institucion });
}

export function invalidateQuizBankCache(institucion = null) {
  if (institucion) {
    const resolved = resolveInstitucion(institucion);
    const state = getBankState(resolved);
    state.memory = null;
    state.lastCheck = 0;
    removeIdbCachedData(getBankCacheKey(resolved)).catch(() => {});
    return;
  }

  _quizBankStates.forEach((state) => {
    state.memory = null;
    state.lastCheck = 0;
  });
  ['semar', 'guardia_nacional', 'sedena'].forEach((key) => {
    const cacheKey = key === 'semar' ? QUIZ_BANK_IDB_KEY : `${QUIZ_BANK_IDB_KEY}_${key}`;
    removeIdbCachedData(cacheKey).catch(() => {});
  });
}

function scheduleKeyRevalidation(key, path, versionPath, ttl) {
  if (_revalidatingKeys.has(key)) return;
  _revalidatingKeys.add(key);
  (async () => {
    try {
      const remoteVersion = versionPath ? await fetchVersionNode(versionPath) : null;
      const cached = getCachedDataWithVersion(key, ttl);
      const hasData = cached && cached.data !== undefined && cached.data !== null;
      const versionChanged = versionPath && remoteVersion != null && cached && cached.version !== remoteVersion;
      if (!hasData || versionChanged) {
        const data = await fetchFromRTDB(path);
        if (data !== null) setCachedData(key, data, remoteVersion);
      }
    } catch {}
    finally {
      _revalidatingKeys.delete(key);
    }
  })();
}

async function fetchFromRTDB(path) {
  const snapshot = await get(ref(rtdb(), path));
  if (!snapshot.exists()) return null;
  return snapshot.val();
}

export async function getCached(path, options = {}) {
  const {
    ttl = CACHE_TTL.STATIC,
    versionPath = null,
    transform = (value) => value,
    fallback = null,
    cacheKey = null,
  } = options;

  const key = cacheKey || buildCacheKey(path);

  if (versionPath) {
    const cached = getCachedDataWithVersion(key, ttl);
    if (cached && isCacheValid(cached, ttl)) {
      // Devolver el caché de inmediato y revalidar la versión en segundo plano.
      scheduleKeyRevalidation(key, path, versionPath, ttl);
      return transform(cached.data);
    }
  } else {
    const cached = getCachedData(key, ttl);
    if (cached !== null) {
      return transform(cached);
    }
  }

  try {
    const [data, version] = await Promise.all([
      fetchFromRTDB(path),
      versionPath ? fetchVersionNode(versionPath) : Promise.resolve(null),
    ]);
    if (data === null) return fallback;

    setCachedData(key, data, version);
    return transform(data);
  } catch (error) {
    console.warn(`[rtdbCache] Error fetching ${path}:`, error);
    return fallback;
  }
}

export async function getCachedAreas(institucion = 'SEMAR') {
  const bank = await loadQuizBank({ institucion });
  return bank.areas;
}

export async function getCachedTemas(institucion = 'SEMAR') {
  const bank = await loadQuizBank({ institucion });
  return bank.temas;
}

export async function getCachedPreguntasPorTema(institucion = 'SEMAR') {
  const bank = await loadQuizBank({ institucion });
  return bank.preguntas;
}

export async function getCachedModelos() {
  return getCached('config/apis/modelos', {
    ttl: CACHE_TTL.CONFIG,
    transform: (value) => {
      if (!value || typeof value !== 'object') return {};
      const models = {};
      Object.entries(value).forEach(([key, modelValue]) => {
        if (typeof modelValue === 'object' && modelValue !== null) {
          models[key] = {
            displayName: modelValue.displayName || key,
            modelName: modelValue.modelName || key,
            apiKey: modelValue.apiKey || modelValue.gemini || '',
            provider: modelValue.provider || 'openrouter',
          };
        } else {
          models[key] = {
            displayName: `${key} (${modelValue})`,
            modelName: modelValue?.toString() || key,
            apiKey: '',
            provider: 'openrouter',
          };
        }
      });
      return models;
    },
    fallback: {},
  });
}

function normalizeEspecialidades(value) {
  if (!value || typeof value !== 'object') return [];
  const entries = Array.isArray(value)
    ? value.map((tema, index) => [String(tema?.id ?? tema?.temaId ?? index), tema])
    : Object.entries(value);

  return entries
    .filter(([, temaObj]) => temaObj && typeof temaObj === 'object' && temaObj.cuestionarios)
    .map(([temaKey, temaObj]) => ({
      temaId: Number.isNaN(parseInt(temaObj.id ?? temaKey, 10)) ? temaKey : parseInt(temaObj.id ?? temaKey, 10),
      areaId: Number(temaObj.area_id ?? temaObj.areaId) || null,
      nombre: temaObj.nombre || `Especialidad Tema ${temaKey}`,
      cuestionarios: temaObj.cuestionarios,
    }));
}

export async function getCachedEspecialidadesTemas(
  hierarchyCode,
  corpsCode,
  specialtyCode,
  institucion = 'SEMAR'
) {
  const resolved = resolveInstitucion(institucion);
  if (!hierarchyCode && !corpsCode) return [];

  const instKey = getInstitutionKey(resolved);
  const cacheKey = buildCacheKey(
    `espec_${instKey}_${hierarchyCode || 'TODOS'}_${corpsCode || 'TODOS'}_${specialtyCode || 'TODOS'}`
  );

  const localCached = getCachedData(cacheKey, CACHE_TTL.USER_SPECIFIC);
  if (Array.isArray(localCached) && localCached.length > 0) {
    return localCached;
  }

  const idbCached = await getIdbCachedData(cacheKey, CACHE_TTL.USER_SPECIFIC);
  if (Array.isArray(idbCached?.data) && idbCached.data.length > 0) {
    return idbCached.data;
  }

  if (_especInFlight.has(cacheKey)) {
    return _especInFlight.get(cacheKey);
  }

  const paths = getSpecialtyPaths(resolved, hierarchyCode, corpsCode, specialtyCode);
  const request = (async () => {
    try {
      for (const path of paths) {
        const data = await fetchFromRTDB(path);
        const result = normalizeEspecialidades(data);
        if (result.length > 0) {
          await setIdbCachedData(cacheKey, result, null);
          return result;
        }
      }
      await setIdbCachedData(cacheKey, [], null);
      return [];
    } catch (error) {
      console.warn('[rtdbCache] Error fetching especialidades_temas:', error);
      return [];
    }
  })();

  _especInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    _especInFlight.delete(cacheKey);
  }
}

export async function getCachedLicenseTokens(encodedEmail) {
  const path = `licencesPremium/${encodedEmail}`;
  const cacheKey = buildCacheKey(`license_${encodedEmail}`);

  const cached = getCachedData(cacheKey, CACHE_TTL.SHORT);
  if (cached !== null) {
    return cached;
  }

  try {
    const data = await fetchFromRTDB(path);
    if (!data) return null;
    const tokens = {
      tokensAvailable: data.tokensAvailable || 2000000,
      tokensUsed: data.tokensUsed || 0,
      expiresAt: data.expiresAt || data.tokenPeriodEnd || null,
    };
    setCachedData(cacheKey, tokens, null);
    return tokens;
  } catch (error) {
    console.warn(`[rtdbCache] Error fetching license for ${encodedEmail}:`, error);
    return null;
  }
}

export function invalidateCache(pattern) {
  try {
    const prefix = buildCacheKey(pattern);
    Object.keys(localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => localStorage.removeItem(key));
  } catch {}

  if (/(quiz_bank|areas|temas|preguntas)/.test(String(pattern))) {
    invalidateQuizBankCache();
  }
}

export function clearAllCache() {
  try {
    removeCachePrefix('vortex_cache_');
  } catch {}
  invalidateQuizBankCache();
  clearIdbCache().catch(() => {});
}
